import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    TextField,
    Drawer,
    useMediaQuery,
    Alert,
    ListItemButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import "./Recommendations.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import ProductCard from "../../components/ProductCard/ProductCard";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup, { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import CropPortraitIcon from "@mui/icons-material/CropPortrait";
import ListIcon from "@mui/icons-material/List";
import ProductList from "../../components/ProductList/ProductList";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MenuIcon from "@mui/icons-material/Menu";
import ScheduleOrder from "../../components/ScheduleOrder/ScheduleOrder";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import { useCart } from "../../context/cartContext";
import { useOptions } from "../../context/optionContext";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { ALERT_MESSAGES, BUTTON, LABELS, PLACEHOLDER, ROUTES, TABLE_HEADERS, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { Add, CheckBox, CheckBoxOutlineBlankOutlined, ShoppingCart, ShoppingCartOutlined } from "@mui/icons-material";
import { 
	getRecommendationsProductList, 
	deleteRecommendationProduct, 
	getRecommendationsByUserAndSite, 
	deleteRecommendation, 
	createRecommendation 
} from "../../services/recommendationsService";
import { getCustomerByIdForCusSites } from "../../services/scheduleOrderService";
import { saveRequestListBulk } from "../../services/rsplService";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import ListPageSkeleton from "../../components/PageSkeletons/ListPageSkeleton";
import ContentInfo from "../../components/ContentInfo/ContentInfo";


const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
	[`& .${toggleButtonGroupClasses.grouped}`]: {
		margin: theme.spacing(0.5),
		marginRight: -1,
		border: 0,
		borderRadius: theme.shape.borderRadius,
		[`&.${toggleButtonGroupClasses.disabled}`]: {
			border: 0,
		},
	},
	[`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
		marginLeft: -1,
		borderLeft: "1px solid transparent",
	},
}));

function Recommendations() {
	const [items, setItems] = useState([]);
	const [selectedItem, setSelectedItem] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [listSearchTerm, setListSearchTerm] = useState("");
	const [open, setOpen] = useState(false);
	const [newListName, setNewListName] = useState("");
	const [newListDescription, setNewListDescription] = useState("");
	const [products, setProducts] = useState([]);
	const [type, setType] = useState("card");
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [sortOption, setSortOption] = useState("");
	const [sortOptionProducts, setSortOptionProducts] = useState("");
	const isMobile = useMediaQuery("(max-width:600px)");
	const [error, setError] = useState("");
	const [isCreatingList, setIsCreatingList] = useState(false);
	const [loading, setLoading] = useState(true);
	const [productName, setProductName] = useState("");
	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [recommendedParts, setRecommendedParts] = useState(null);
	const selectedDrawerItemRef = useRef(null);
	const selectedDesktopItemRef = useRef(null);
	const navigate = useNavigate();
	const { addMultipleToCart } = useCart();
	const { optionKeys } = useOptions();
	const { selectedSite, siteData, selectedSiteCurrency, currencySymbol, selectedSiteData } = useSite();
	const { user, hasPermission } = useAuth();
	const orderSchedulesEnabled = optionKeys?.enableOrderSchedules !== "false";
	const isListOwner = (ownerId) => Number(ownerId) === Number(user?.user_id);
	const canDeleteRecommendationList = !!hasPermission("recommendations_management.delete_list");
	const { t } = useTranslation();
	const canManageSelectedList = !!(selectedItem && (isListOwner(selectedItem?.user_id) || canDeleteRecommendationList));
	const [selectedProductIds, setSelectedProductIds] = useState(new Set());

	useEffect(() => {
		if (!selectedItem?.id) return;
		if (loading) return;

		const getScrollParent = (el) => {
			let node = el?.parentElement;
			while (node) {
				const style = window.getComputedStyle(node);
				const overflowY = style.overflowY;
				const canScroll = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
				if (canScroll) return node;
				node = node.parentElement;
			}
			return document.scrollingElement || document.documentElement;
		};

		const centerItem = (itemEl) => {
			if (!itemEl) return;
			const container = getScrollParent(itemEl);
			if (!container) return;
			const containerRect = container.getBoundingClientRect();
			const itemRect = itemEl.getBoundingClientRect();
			const itemTopWithinContainer = itemRect.top - containerRect.top;
			const targetScrollTop =
				container.scrollTop +
				itemTopWithinContainer -
				(container.clientHeight / 2 - itemRect.height / 2);
			container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
		};

		let raf1 = 0;
		let raf2 = 0;
		raf1 = window.requestAnimationFrame(() => {
			raf2 = window.requestAnimationFrame(() => {
				centerItem(selectedDrawerItemRef.current);
				centerItem(selectedDesktopItemRef.current);
			});
		});
		return () => {
			window.cancelAnimationFrame(raf1);
			window.cancelAnimationFrame(raf2);
		};
	}, [selectedItem?.id, loading, listSearchTerm, sortOption, drawerOpen, isMobile, items.length]);
	const [checkoutDetails, setCheckoutDetails] = useState({
		shipping_address: {
			name: "",
			street: "",
			city: "",
			zip: "",
			country: "",
			state: "",
		},
		billing_address: {
			name: "",
			street: "",
			city: "",
			zip: "",
			country: "",
			state: "",
		},
		additional_details: {
			delivery_terms: "UI_TEXT.PREPAID_AND_ADD",
			ship_complete: t('MENU_ITEMS.NO'),
			mode_of_delivery: "UPS Ground",
			freight_number: "",
			order_notes: "",
		},
		payment_details: {
			payment_mode: "PO",
			payment_details: {
				po_number: " ",
				po_file: "",
			},
		},
		order_details: {
			products: [
				{
					product_id: "",
					ProductQty: "",
					ProductPrice: "",
					total: "",
				},
			],
			taxes: "",
			shipping: "",
			discounts: "",
			total: "",
			currency: selectedSiteCurrency || "usd",
		},
	});

	const fetchProductsForList = useCallback(
		async (listId) => {
			if (!listId) {
				setProducts([]);
				return;
			}
			try {
				const req_body = { list_id: listId, site_number: selectedSite };
				const response = await getRecommendationsProductList(req_body);
				console.log("Inside recommendations, getrecomendationsproductlist response", response);
				if (response.status) {
					setProducts(response.data || []);
				} else {
					setProducts([]);
					console.error("Failed to fetch products:", response.error);
				}
			} catch (error) {
				setProducts([]);
				console.error("Error fetching products:", error);
			}
		},
		[selectedSite]
	);

	const sortOptions = [t('GENERAL.NAME_ASC'), t('GENERAL.NAME_DESC')];
	const sortOptionsProducts = [t('GENERAL.NAME_ASC'), t('GENERAL.NAME_DESC'), t('GENERAL.NUMBER_ASC'), t('GENERAL.NUMBER_DESC'), t('GENERAL.PRICE_ASC'), t('GENERAL.PRICE_DESC')];

	const handleSortChange = (value) => {
		setSortOption(value);
		if (value === t('GENERAL.NAME_ASC')) {
			setItems((prevProducts) => [...prevProducts].sort((a, b) => a.name.localeCompare(b.name)));
		} else if (value === t('GENERAL.NAME_DESC')) {
			setItems((prevProducts) => [...prevProducts].sort((a, b) => b.name.localeCompare(a.name)));
		}
	};

	const handleSortChangeProducts = (value) => {
		setSortOptionProducts(value);

		if (value === t('GENERAL.NAME_ASC')) {
			setProducts((prevProducts) =>
				[...prevProducts].sort((a, b) => a.product_name.trim().localeCompare(b.product_name.trim()))
			);
		} else if (value === t('GENERAL.NAME_DESC')) {
			setProducts((prevProducts) =>
				[...prevProducts].sort((a, b) => b.product_name.trim().localeCompare(a.product_name.trim()))
			);
		} else if (value === t('GENERAL.NUMBER_ASC')) {
			setProducts((prevProducts) =>
				[...prevProducts].sort((a, b) => a.product_number.trim().localeCompare(b.product_number.trim()))
			);
		} else if (value === t('GENERAL.NUMBER_DESC')) {
			setProducts((prevProducts) =>
				[...prevProducts].sort((a, b) => b.product_number.trim().localeCompare(a.product_number.trim()))
			);
		} else if (value === t('GENERAL.PRICE_ASC')) {
			setProducts((prevProducts) =>
				[...prevProducts].sort((a, b) => a.product_sell_price - b.product_sell_price)
			);
		} else if (value === t('GENERAL.PRICE_DESC')) {
			setProducts((prevProducts) =>
				[...prevProducts].sort((a, b) => b.product_sell_price - a.product_sell_price)
			);
		}
	};

	const handleItemClick = async (item) => {
		if (!item) return;
		setSelectedItem(item);
		await fetchProductsForList(item.id);
	};

	// Always reset bulk selections when switching lists
	useEffect(() => {
		setSelectedProductIds(new Set());
	}, [selectedItem?.id]);

	const handleType = (event, newAlignment) => {
		// Prevent setting type to null - keep current value if newAlignment is null
		if (newAlignment !== null) {
			setType(newAlignment);
		}
	};


	const handleRemoveProduct = async (productID, machineID) => {
		try {
			const req_body = {
				list_id: selectedItem.id,
				product_id: productID,
				machine_id: machineID,
			};

			const response = await deleteRecommendationProduct(req_body);
			console.log("Inside recommendations, deleterecommendationsproduct response",response);
			if (response?.status) {
				setProducts((prevProducts) =>
					prevProducts.filter(
						(product) =>
							!(
								product.product_id === productID &&
								(product.machine_id === machineID || product.machine_id === Number(machineID))
							)
					)
				);

				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.REMOVED'),
					text: t('ALERT_MESSAGES.PART_SUCCESSFULLY_REMOVED_LIST'),
				});
			} else {
				const errorMessage = response?.error || response?.message || t('ALERT_MESSAGES.UNKNOWN_ERROR');
				console.error("Failed to remove part:", errorMessage);
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: errorMessage || t('ALERT_MESSAGES.FAILED_TO_REMOVE_PART'),
				});
			}
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.AN_ERROR_OCCURED_REMOVING_PART'),
			});
		}
	};

    useEffect(() => {
        const fetchRecommendationsAndProducts = async () => {
            setLoading(true);
            try {
                const user_id = user.user_id;
                const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;

                if (!site_id) {
                    console.error(t('GENERAL.SITE_ID_NOT_FOUND'));
                    // Clear selections when no site is available
                    setItems([]);
                    setSelectedItem(null);
                    setProducts([]);
                    return;
                }

				// Fetch recommendations
				const recommendationsResponse = await getRecommendationsByUserAndSite(user_id, site_id);
				console.log("Inside recommendations, getrecomendationsbyuserandsite response",recommendationsResponse);
				if (recommendationsResponse.status) {
					const recommendations = recommendationsResponse.data;
					setItems(recommendations);

                    if (recommendations.length > 0) {
                        const firstRecommendation = recommendations[0];
                        setSelectedItem(firstRecommendation);

						// Fetch products for the first recommendation
						await fetchProductsForList(firstRecommendation.id);
					} else {
						setItems([]);
						setSelectedItem(null);
						setProducts([]);
					}
				} else {
					setItems([]);
					setSelectedItem(null);
					setProducts([]);
					console.error("Failed to fetch recommendations:", recommendationsResponse.error);
				}
			} catch (error) {
				console.error("Error fetching recommendations or products:", error);
			} finally {
				setLoading(false);
			}
		};

        if (selectedSite) fetchRecommendationsAndProducts();
    }, [selectedSite, siteData, user.user_id, fetchProductsForList]);

	const handleDeleteItem = async (id) => {
		if (!canDeleteRecommendationList) {
			Swal.fire({
				icon: "info",
				title: t('ALERT_MESSAGES.INFO', { defaultValue: "Info" }),
				text: t('ALERT_MESSAGES.NO_PERMISSION', { defaultValue: "You do not have permission to delete lists." }),
			});
			return;
		}
		try {
			const confirmation = await Swal.fire({
				title: "Are you sure?",
				text: "This will delete this list permanently.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#6b7280",
				confirmButtonText:  t('GENERAL.YES_DELETE_IT'),
			});

			if (!confirmation.isConfirmed) return;
			const user_id = user.user_id;
			const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;

			if (!site_id) {
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: t('ALERT_MESSAGES.FAILED_TO_DELETE_THE_LIST'),
				});
				return;
			}

			const response = await deleteRecommendation(id, { user_id, site_id });
			console.log("Inside recommendations, deleterecommendation response",response);
			if (response.status) {
				let nextSelection = selectedItem;
				setItems((prevItems) => {
					const updatedItems = prevItems.filter((item) => item.id !== id);
					if (selectedItem?.id === id) {
						nextSelection = updatedItems[0] || null;
					}
					return updatedItems;
				});

				if (selectedItem?.id === id) {
					if (nextSelection) {
						setSelectedItem(nextSelection);
						await fetchProductsForList(nextSelection.id);
					} else {
						setSelectedItem(null);
						setProducts([]);
					}
				}

				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.DELETED', { defaultValue: "Deleted" }),
					text: t('ALERT_MESSAGES.LIST_DELETED_SUCCESSFULLY', { defaultValue: "Recommendation list deleted successfully." }),
				});
			} else {
                const errorMessage = response?.message || t('ALERT_MESSAGES.FAILED_TO_DELETE_THE_LIST');
				console.error("Failed to delete list:", errorMessage);
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: errorMessage, // Display the backend message
				});
			}
		} catch (error) {
			console.error("Error deleting list:", error);
            let errorMessage = t('ALERT_MESSAGES.FAILED_TO_DELETE_THE_LIST');
            if (error.message === "Network Error" || error.code === "ERR_NETWORK") {
                errorMessage = t('GENERAL.NETWORK_ERROR_COULD_NOT_CONNECT_TO_SERVER_PLEASE_ENSURE_THE_BACKEND_IS_RUNNING');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: errorMessage,
			});
		}
	};

	const filteredItems = items.filter((item) => item?.name?.toLowerCase().includes(listSearchTerm.toLowerCase()));

	const visibleProducts = useMemo(
		() =>
			products.filter((product) =>
				product?.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
			),
		[products, searchTerm]
	);

	const areAllVisibleSelected = useMemo(() => {
		if (!visibleProducts.length) return false;
		return visibleProducts.every((p) => selectedProductIds.has(String(p.product_id)));
	}, [visibleProducts, selectedProductIds]);

	const handleToggleProductSelection = useCallback((product) => {
		if (!product?.product_id) return;
		const id = String(product.product_id);
		setSelectedProductIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const handleToggleSelectAll = useCallback(() => {
		setSelectedProductIds((prev) => {
			if (areAllVisibleSelected) return new Set();
			const next = new Set(prev);
			visibleProducts.forEach((p) => {
				if (p?.product_id) next.add(String(p.product_id));
			});
			return next;
		});
	}, [areAllVisibleSelected, visibleProducts]);

	const handleSearchChange = (value) => {
		setSearchTerm(value);
	};

	const handleListSearchChange = (value) => {
		setListSearchTerm(value);
	};

	const onSaveForLater = (id) => {
		console.log(`Product ${id} added to Saved For Later`);
	};

	const handleOpenDialog = () => {
		setOpen(true);
	};

	const handleCloseDialog = () => {
		setOpen(false);
		setNewListName("");
		setNewListDescription("");
		setError("");
	};

	const handleScheduleOrder = (row = undefined) => {
		setScheduleOpen(true);
		setRecommendedParts(row);
	};

	useEffect(() => {
		const fetchCustomerData = async () => {
			try {
				const customer_resp = await getCustomerByIdForCusSites(selectedSiteData.cus_id);
				console.log("Inside recommendations, getcustomeridforcussites response",customer_resp);
				setCheckoutDetails({
					...checkoutDetails,
					billing_address: {
						name: `${customer_resp.data.address_name_cus}`,
						street: `${customer_resp.data.street1_cus} ${customer_resp.data.street2_cus} ${customer_resp.data.street3_cus}`,
						city: `${customer_resp.data.city_cus}`,
						zip: `${customer_resp.data.zip_cus}`,
						country: `${customer_resp.data.country_cus}`,
						state: `${customer_resp.data.state_cus}`,
					},
					shipping_address: {
						name: `${selectedSiteData.address_name_ln} - ${selectedSiteData.customer_site_number}`,
						street: `${selectedSiteData.street1_ln} ${selectedSiteData.street2_ln} ${selectedSiteData.street3_ln}`,
						city: `${selectedSiteData.city_ln}`,
						zip: `${selectedSiteData.zip_ln}`,
						country: `${selectedSiteData.country_ln}`,
						state: `${selectedSiteData.state_ln}`,
					},
				});
			} catch (error) {
				console.log(error);
			}
		};

		// if (selectedSite) {
		console.log("Selected Site: ", selectedSite);
		console.log("Site Data: ", selectedSiteData);
		fetchCustomerData();
		// }
	}, [selectedSite]);

	useEffect(() => {
		if (!orderSchedulesEnabled && scheduleOpen) {
			setScheduleOpen(false);
		}
	}, [orderSchedulesEnabled, scheduleOpen]);

	const transformRecommendationProducts = (products) => {
		// Filter out non-purchasable products
		const purchasableProducts = products.filter(product => Number(product.purchasable) === 1);
		const prods = purchasableProducts.map((item) => {
			const product = item;
			const price =
				product?.[`price_${selectedSiteCurrency}`] ??
				product?.product_sell_price ??
				0;

			return {
				id: null, // Keep as is
				order_id: product.product_id,
				product_id: product.product_id,
				product_quantity: item.Quantity || 1,
				product_price: price.toString(),
				product_total: price.toString(),
				productsData: product
			};
		});
		console.log(selectedSite, purchasableProducts);
		const cart_products = purchasableProducts.map((item) => ({
			product_id: item.product_id,
			product_name: item.product_name,
			product_number: item.product_number,
			ProductQty: item.Quantity,
			ProductPrice: item[`price_${selectedSiteCurrency}`],
			total: (item.Quantity * parseFloat(item[`price_${selectedSiteCurrency}`])).toFixed(2),
		}));

		const productTotal = cart_products.reduce((total, item) => total + parseFloat(item.total), 0);
		const taxes = (productTotal * 0.08).toFixed(2);
		const discount = (productTotal * 0.05).toFixed(2);
		const shipping = 50.0;
		const total = (productTotal + parseFloat(taxes) - parseFloat(discount) + shipping).toFixed(2);
		return {
			shipping_address: checkoutDetails.shipping_address,
			billing_address: checkoutDetails.billing_address,
			additional_details: checkoutDetails.additional_details,
			payment_details: checkoutDetails.payment_details,
			order_details: {
				products: cart_products,
				taxes: taxes,
				shipping: shipping,
				discounts: discount,
				total: total,
			},
			selectedSite: selectedSite,
			loggedin_user: user.user_id,
			loggedin_user_email: user.user_email,
			loggedin_user_name: user.user_first_name + " " + user.user_last_name,
			loggedin_user_phone: user.user_phone || "",
			products: prods,
		}
	};

	const handleBulkAddSelected = async () => {
		const selectedIds = Array.from(selectedProductIds || []);
		if (!selectedIds.length) {
			Swal.fire({
				icon: "info",
				title: t('ALERT_MESSAGES.INFO'),
				text: t('GENERAL.PLEASE_SELECT_AT_LEAST_ONE_PART'),
			});
			return;
		}

		if (!selectedSite) {
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('GENERAL.PLEASE_SELECT_A_SITE_FIRST'),
			});
			return;
		}

		const selectedProducts = products.filter((p) => selectedProductIds.has(String(p.product_id)));
		const purchasable = selectedProducts.filter((p) => Number(p.purchasable) === 1);
		const nonPurchasable = selectedProducts.filter((p) => Number(p.purchasable) !== 1);

		const purchasableProducts = purchasable.map((p) => ({
			ProductID: p.product_id,
			Quantity: Number(p.Quantity) || 1,
			Site: selectedSite,
		}));

		const nonPurchasableProducts = nonPurchasable.map((p) => ({
			machine_id: p.machine_id,
			product_id: p.product_id,
			site_number: selectedSite,
			user_id: user?.user_id,
			quantity: Number(p.Quantity) || 1,
		}));

		const hasPurchasable = purchasableProducts.length > 0;
		const hasNonPurchasable = nonPurchasableProducts.length > 0;

		const addToRequests = async (payload, { successMessage } = {}) => {
			try {
				const resp = await saveRequestListBulk(payload);
				if (resp?.status) {
					if (successMessage) Swal.fire(t('ALERT_MESSAGES.SUCCESS'), successMessage, t('ALERT_MESSAGES.SUCCESS'));
					return true;
				}
				Swal.fire("Error", resp?.message || t('CART.FAILED_TO_ADD_PARTS_TO_REQUEST_LIST'), "error");
			} catch (e) {
				console.error(e);
				Swal.fire("Error", t('CART.FAILED_TO_ADD_PARTS_TO_REQUEST_LIST'), "error");
			}
			return false;
		};

		try {
			// Case 1: Only non-purchasable
			if (hasNonPurchasable && !hasPurchasable) {
				Swal.fire({
					icon: "info",
					title: t('CART.ADD_PARTS_TO_CART_AND_REQUEST_LIST'),
					text: t('GENERAL.SOME_PARTS_CANNOT_BE_PURCHASED_DIRECTLY_DO_YOU_WANT_TO_ADD_THEM_TO_THE_REQUEST_LIST_AS_WELL'),
					showCancelButton: true,
					confirmButtonText: t('GENERAL.YES_ADD_TO_REQUEST_LIST'),
					cancelButtonText: t('BUTTON.CANCEL'),
				}).then(async (result) => {
					if (!result.isConfirmed) return;
					const ok = await addToRequests(nonPurchasableProducts, { successMessage: null });
					if (ok) {
						Swal.fire(t('ALERT_MESSAGES.SUCCESS'), t('GENERAL.PARTS_ADDED_TO_REQUEST_LIST'), "success");
						setSelectedProductIds(new Set());
						navigate("/requests");
					}
				});
				return;
			}

			// Case 2: Both
			if (hasNonPurchasable && hasPurchasable) {
				Swal.fire({
					icon: "info",
					title: t('CART.ADD_PARTS_TO_CART_AND_REQUEST_LIST'),
					text: t('GENERAL.SOME_PARTS_CANNOT_BE_PURCHASED_DIRECTLY_DO_YOU_WANT_TO_ADD_THEM_TO_THE_REQUEST_LIST_AS_WELL'),
					showCancelButton: true,
					showDenyButton: true,
					confirmButtonText: t('CART.YES_ADD_BOTH'),
					denyButtonText: t('CART.NO_JUST_ADD_TO_CART'),
					cancelButtonText: t('BUTTON.CANCEL'),
				}).then(async (result) => {
					if (result.isConfirmed) {
						const ok = await addToRequests(nonPurchasableProducts, { successMessage: null });
						if (ok) {
							await addMultipleToCart(purchasableProducts);
							Swal.fire(t('ALERT_MESSAGES.SUCCESS'), t('GENERAL.PARTS_ADDED_TO_CART_AND_REQUEST_LIST'), "success");
							setSelectedProductIds(new Set());
						}
					} else if (result.isDenied) {
						await addMultipleToCart(purchasableProducts);
						Swal.fire(t('ALERT_MESSAGES.SUCCESS'), t('GENERAL.PURCHASABLE_PARTS_ADDED_TO_CART'), "success");
						setSelectedProductIds(new Set());
					}
				});
				return;
			}

			// Case 3: Only purchasable
			if (!hasNonPurchasable && hasPurchasable) {
				await addMultipleToCart(purchasableProducts);
				Swal.fire(t('ALERT_MESSAGES.SUCCESS'), selectedProducts.length + " " + t('GENERAL.ADDED_TO_CART_1').replace(':', '') + " parts", "success");
				setSelectedProductIds(new Set());
				return;
			}

			Swal.fire("No Parts", t('GENERAL.NO_VALID_PARTS_SELECTED'), "info");
		} catch (error) {
			console.error("Bulk add error:", error);
			Swal.fire("Error", t('GENERAL.FAILED_TO_ADD_SELECTED_PARTS'), "error");
		}
	};

	const handleCreateList = async () => {
		if (isCreatingList) return;
		const trimmedName = newListName.trim();
		if (!trimmedName) {
			setError(t('ALERT_MESSAGES.LIST_NAME_REQUIRED', { defaultValue: (t('GENERAL.LIST_NAME_IS_REQUIRED')) }));
			return;
		}
		setIsCreatingList(true);
		try {
			const user_id = user.user_id;
			const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;

			if (!site_id) {
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: t('ALERT_MESSAGES.FAILED_TO_DELETE_THE_LIST'),
				});
				return;
			}

			const newList = {
				name: trimmedName,
				description: newListDescription || "",
				user_id,
				site_id,
			};

			const normalizedName = trimmedName.toLowerCase();
			const listExists = items.some(
				(item) =>
					item?.site_id === site_id &&
					item?.user_id === user_id &&
					item?.name?.trim().toLowerCase() === normalizedName
			);

			if (listExists) {
				setError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS', { defaultValue:t('GENERAL.LIST_NAME_ALREADY_EXISTS') }));
				return;
			}
			const response = await createRecommendation(newList);
			console.log("Inside recommendations, create recommendations response",response);
			if (response.status) {
				const updatedResponse = await getRecommendationsByUserAndSite(user_id, site_id);
				console.log("Inside recommendations, getrecommendationsbyuserandsite response",updatedResponse);
				if (updatedResponse.status) {
					setItems(updatedResponse.data);

					const newListItem = updatedResponse.data.find((item) => item.name === newListName);
					setSelectedItem(newListItem || null);
					setProducts([]);
				} else {
					console.error(t('GENERAL.FAILED_TO_REFRESH_RECOMMENDATIONS'), updatedResponse.error);
				}

				handleCloseDialog();
			} else {
				console.error("Failed to create list:", response.error);
				setError(t('ALERT_MESSAGES.FAILED_TO_CREATE_THE_LIST', { defaultValue: "Failed to create the list. Please try again." }));
			}
		} catch (error) {
			console.error("Error creating list:", error);
			setError(t('ALERT_MESSAGES.FAILED_TO_CREATE_THE_LIST', { defaultValue: "Failed to create the list. Please try again." }));
		} finally {
			setIsCreatingList(false);
		}
	};

	const toggleDrawer = () => {
		setDrawerOpen(!drawerOpen);
	};

	const handleAddProductsNaviagte = () => {
		navigate(ROUTES.RSPL);
	};

	const noListsAvailable = items.length === 0;
	const hasSelectedList = Boolean(selectedItem);
	const hasPurchasableProducts = useMemo(
		() => products.some((product) => Number(product?.purchasable) === 1),
		[products]
	);
	const canCreateOrderSchedule =
		orderSchedulesEnabled && hasPermission("order_management.create_order_schedules") && hasSelectedList && hasPurchasableProducts;

	if (loading && products.length === 0 && items.length === 0) {
		return <ListPageSkeleton showAction={hasPermission("recommendations_management.create_list")} />;
	}

	return (
		<Box className="recommendation-page" sx={{ padding: isMobile ? "10px" : "40px" }}>
			<PageTitle title={t('LABELS.RECOMMENDATION')} />

			<Box display="flex" justifyContent={isMobile ? "space-between" : "flex-end"} mb={2}>
				{isMobile && (
					<IconButton onClick={toggleDrawer}>
						<MenuIcon />
					</IconButton>
				)}
				{hasPermission("recommendations_management.create_list") && (
					<Button
						variant="contained"
						color="primary"
						size="medium"
						startIcon={<Add />}
						sx={{ ml: 2 }}
						onClick={handleOpenDialog}
						data-tour="recommendations-create-list">
						{t('BUTTON.CREATE_LIST')}
					</Button>
				)}
			</Box>

			<ModalWrapper
				open={open}
				setOpen={setOpen}
				title={t('UI_TEXT.CREATE_A_NEW_LIST')}
				content={
					<>
						{error && (
							<Alert severity="error" sx={{ mb: 1 }}>
								{error}
							</Alert>
						)}
						<TextField
							autoFocus
							margin="dense"
							label={t('LABELS.LIST_NAME')}
							type="text"
							fullWidth
							required
							size="small"
							value={newListName}
							error={!!error}
							onChange={(e) => {
								setNewListName(e.target.value);
								if (error) setError("");
							}}
							sx={{ mb: 1 }}
						/>
						<TextField
							margin="dense"
							label={t('LABELS.LIST_DESCRIPTION')}
							type="text"
							fullWidth
							multiline
							size="small"
							rows={4}
							value={newListDescription}
							onChange={(e) => setNewListDescription(e.target.value)}
						/>
					</>
				}
				actions={
					<>
						<Button
							onClick={handleCloseDialog}
							color="primary"
							variant="outlined"
							size="medium"
						>
							{t('BUTTON.CANCEL')}
						</Button>
						<Button
							onClick={handleCreateList}
							color="primary"
							variant="contained"
							size="medium"
							disabled={isCreatingList}
							sx={{ backgroundColor: "var(--primary-main)" }}>
							{t('BUTTON.CREATE_LIST')}
						</Button>
					</>
				}
			/>

			<Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
				<Box width="250px" p={2}>
					<ContextFilterAndSort
						value={listSearchTerm}
						onSearchChange={handleListSearchChange}
						placeholder={PLACEHOLDER.SEARCH_LIST}
						options={sortOptions}
						selectedOption={sortOption}
						onSortChange={handleSortChange}
						showSort
						showSearch
					/>
					<List style={{ overflowY: "visible", maxHeight: "none" }} data-tour="recommendations-view-lists"> 
						{filteredItems.length > 0 ? (
							filteredItems.map((item) => {
								const canDeleteThisSpecificList = canDeleteRecommendationList && (item.user_id === user?.user_id);
								return (
									<>
										<ListItemButton
											key={item.id}
											ref={selectedItem?.id === item.id ? selectedDrawerItemRef : null}
											selected={selectedItem?.id === item.id}
											onClick={() => handleItemClick(item)}
											sx={{
												my: 0.25,
												cursor: "pointer",
												borderRadius: "10px",
												"&:hover": {
													color: "var(--primary-main)",
													transition: "0.2s",
												},
											}}>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',

													rowGap: 0,
													columnGap: 2,
													px: 2,
													py: 0.25,
													color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
													'& svg': {
														color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
													},
												}}
											>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'flex-start',
														alignItems: 'flex-start',
														flexDirection: 'column',
														rowGap: 0,
														columnGap: 2, color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
														'& svg': {
															color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
														},
													}}>

													<Typography variant="body1" fontWeight={"bold"}>
														{item.name}
													</Typography>
													<Typography variant="body1" color={selectedItem?.id === item.id ? 'primary.main' : "text.secondary"} fontSize={"11px"}>
														<b>Created By:</b> {item?.user_name || "N/A"}
													</Typography>
												</Box>

												{canDeleteThisSpecificList && (
											<IconButton
												edge="end"
												aria-label="delete"
												onClick={(e) => {
													e.stopPropagation();
													handleDeleteItem(item.id);
												}}
												style={{
													color: selectedItem?.id === item.id ? "var(--primary-main)" : "inherit",
												}}>
												<DeleteIcon />
											</IconButton>
										)}

											</Box>

										</ListItemButton>

									</>
								);
							})
						) : (
							<div
								style={{
									textAlign: "center",
									padding: "20px",
									color: "gray",
									fontStyle: "italic",
									fontSize: "16px",
									height: "100%",
								}}>
								{t('UI_TEXT.NO_ITEMS_TO_DISPLAY')}
							</div>
						)}
					</List>
				</Box>
			</Drawer>

			<Box display="flex" height="auto">
				{!isMobile && (
					<Box
						width={isMobile ? "100%" : "20%"}
						pr={2}
						style={{ padding: "8px", height: "auto", overflow: "visible" }}>
						<Box display="flex" justifyContent="center" alignItems="center">
							<ContextFilterAndSort
								value={listSearchTerm}
								onSearchChange={handleListSearchChange}
								placeholder={t('PLACEHOLDER.SEARCH_LIST')}
								options={sortOptions}
								selectedOption={sortOption}
								onSortChange={handleSortChange}
								showSort
								showSearch
							/>
						</Box>
						<List style={{ overflowY: "visible", maxHeight: "none" }} data-tour="recommendations-view-lists">
							{filteredItems.length > 0 ? (
								filteredItems.map((item) => {
								const canDeleteThisSpecificList = canDeleteRecommendationList && (item.user_id === user?.user_id);
								return (
									<ListItemButton
										key={item.id}
										ref={selectedItem?.id === item.id ? selectedDesktopItemRef : null}
										selected={selectedItem?.id === item.id}
										onClick={() => handleItemClick(item)}
										sx={{
											my: 0.25,
											cursor: "pointer",
											borderRadius: "10px",
											"&:hover": {
												color: "var(--primary-main)",
												transition: "0.2s",
											},
										}}>
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												width: "100%",
												rowGap: 0,
												columnGap: 2,
												px: 1,
												py: 0.25,
												color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
												'& svg': {
													color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
												},
											}}
										>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'flex-start',
													alignItems: 'flex-start',
													flexDirection: 'column',
													rowGap: 0,
													columnGap: 2, color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
													'& svg': {
														color: selectedItem?.id === item.id ? 'primary.main' : '#252529',
													},
												}}>

												<Typography variant="body1" fontWeight={"bold"}>
													{item.name}
												</Typography>
												<Typography variant="body1" color={selectedItem?.id === item.id ? 'primary.main' : "text.secondary"} fontSize={"11px"}>
													<b>Created By:</b> {item?.user_display_name || "N/A"}
												</Typography>
											</Box>

											{canDeleteThisSpecificList && (
												<IconButton
													edge="end"
													aria-label="delete"
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteItem(item.id);
													}}
													style={{
														color: selectedItem?.id === item.id ? "var(--primary-main)" : "inherit",
													}}>
													<DeleteIcon />
												</IconButton>
											)}

										</Box>

									</ListItemButton>
								);
							})
							) : (
								<div
									style={{
										textAlign: "center",
										padding: "20px",
										color: "gray",
										fontStyle: "italic",
										fontSize: "16px",
										height: "100%",
									}}>
									{t('UI_TEXT.NO_LISTS_FOUND')}
								</div>
							)}
						</List>
					</Box>
				)}

				{!isMobile && <Divider flexItem orientation="vertical" sx={{ ml: 3, my: 0.5 }} />}

				<Box
					width={isMobile ? "100%" : "80%"}
					height="100%"
					px={2}
					pb={2}
					pt={0.5}
					borderRadius={1}
					justifyContent="flex-start"
					style={{ borderColor: "#ddd", height: "100%" }}
					display="flex"
					flexDirection="column">
					{noListsAvailable ? (
						<ContentInfo>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2, justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
								<Typography variant="body1">
									{t('UI_TEXT.NO_LISTS_FOUND') || "No lists available yet."}
								</Typography>
								{hasPermission("recommendations_management.create_list") && (
									<Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog} >
										{t('BUTTON.CREATE_LIST')}
									</Button>
								)}
							</Box>
						</ContentInfo>
					) : !hasSelectedList ? (
						<ContentInfo>
							{t('UI_TEXT.SELECT_LIST') || "Select a list to view recommended parts."}
						</ContentInfo>
					) : (
						<>
									<Box
										display="flex"
										justifyContent="space-between"
										mb={2}
										alignItems={isMobile ? "flex-start" : "flex-start"}
										flexDirection={isMobile ? "column" : "row"}
										width={isMobile ? "100%" : "auto"}>
										<Box width="100%">
											<Typography variant="h6">{selectedItem?.name}</Typography>
											{selectedItem?.description && (
												<Typography variant="body1" color="textSecondary" sx={{ width: "100%" }}>
													<b>{t('TABLE_HEADERS.DESCRIPTION')}:</b> {selectedItem.description}
												</Typography>
											)}
											{selectedItem?.user_display_name && (
												<Typography variant="body1" color="text.secondary">
													<b>{t('TABLE_HEADERS.CREATED_BY')}:</b> {selectedItem?.user_display_name}
												</Typography>
											)}
										</Box>
										<Box
											display="flex"
											flexDirection="row"
											justifyContent={isMobile ? "space-between" : ""}
											gap="10px"
											alignItems={"center"}
											width={isMobile ? "100%" : "auto"}
											sx={{ marginTop: isMobile ? 2 : 0 }}>
											{products?.length > 0 && (
												<>
													<Button
														variant="outlined"
														size="small"
														onClick={handleToggleSelectAll}
														disabled={!visibleProducts.length}
														startIcon={areAllVisibleSelected ? <CheckBox /> : <CheckBoxOutlineBlankOutlined />}
													>
														{areAllVisibleSelected ? t('BUTTON.DESELECT_ALL') : t('BUTTON.SELECT_ALL')}
													</Button>
													<Tooltip title={t('LABELS.ADD_TO_CART')} arrow>
														<span>
															<Button
																variant="contained"
																size="small"
																onClick={handleBulkAddSelected}
																disabled={!selectedProductIds.size}
																startIcon={<ShoppingCartOutlined />}
															>
																{t('LABELS.ADD_TO_CART')}
															</Button>
														</span>
													</Tooltip>
												</>
											)}
											{canCreateOrderSchedule && (
												<Tooltip title={t('TITLES.CREATE_ORDER_SCHEDULE')} arrow>
													<Button
														variant="contained"
														color="primary"
														size="small"
														onClick={() => handleScheduleOrder()}
														startIcon={<AddCircleOutlineIcon />}
														sx={{
															alignSelf: 'center',
															maxWidth: '300px',
															paddingRight: '12px',
															minWidth: '200px',
														}}
													>
														<Box
															component="span"
															sx={{
																overflow: 'hidden',
																textOverflow: 'ellipsis',
																whiteSpace: 'nowrap',
																display: 'inline-block',
																verticalAlign: 'middle',
																maxWidth: '100%',
															}}
														>
															{t('TITLES.CREATE_ORDER_SCHEDULE')}
														</Box>
													</Button>
												</Tooltip>
											)}
											<StyledToggleButtonGroup
												size="small"
												value={type}
												exclusive
												onChange={handleType}
												aria-label="text alignment">
												<Tooltip title={t('UI_TEXT.CARD_VIEW', { defaultValue: 'Card View' })} arrow>
													<ToggleButton value="card" aria-label="centered">
														<CropPortraitIcon />
													</ToggleButton>
												</Tooltip>
												<Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 0.5 }} />
												<Tooltip title={t('UI_TEXT.LIST_VIEW', { defaultValue: 'List View' })} arrow>
													<ToggleButton value="list" aria-label="centered">
														<ListIcon />
													</ToggleButton>
												</Tooltip>
											</StyledToggleButtonGroup>
											<Box width={isMobile ? "100%" : "250px"}>
												<ContextFilterAndSort
													value={searchTerm}
													onSearchChange={handleSearchChange}
													placeholder={t('PLACEHOLDER.SEARCH_SELECTED')}
													options={sortOptionsProducts}
													selectedOption={sortOptionProducts}
													onSortChange={handleSortChangeProducts}
													showSearch={true}
													showSort={true}
												/>
											</Box>

										</Box>
									</Box>

									<Box
										display="flex"
										flexWrap="wrap"
										justifyContent={isMobile ? "center" : "flex-start"}
										paddingLeft="15px"
										gap="10px"
									>
										{visibleProducts.length === 0 ? (
											<Box
												display="flex"
												justifyContent="center"
												alignContent="center"
												flexDirection={"column"}
												width="100%"
												alignItems="center"
												rowGap={2}
												height="400px">
												<Alert severity="info">{t('ALERT_MESSAGES.NO_PARTS_FOUND')}</Alert>
												{selectedItem &&
													(selectedItem.user_id === user?.user_id && hasPermission("recommendations_management.add_parts")) && (
														<Button variant="contained" onClick={handleAddProductsNaviagte}>
															{t('BUTTON.ADD_PARTS')}
														</Button>
													)}
											</Box>
										) : type === "card" ? (
												visibleProducts.map((product) => (
													<Box key={product.product_id} marginY="10px">
														<ProductCard
															product={product}
															machineID={product.machine_id}
															onSaveForLater={onSaveForLater}
															onDelete={handleRemoveProduct}
															showSavedForLaterDial={!canManageSelectedList}
															showDial={!!canManageSelectedList}
															showCheckbox={true}
															checked={selectedProductIds.has(String(product.product_id))}
															onCheckedChange={handleToggleProductSelection}
														/>
													</Box>
												))
										) : selectedSiteCurrency ? (
											<ProductList
												style={{
													width: "100%",
													overflowY: "auto",
													maxHeight: "500px",
												}}
														products={visibleProducts}
												onRemoveProduct={(productId, machineId) => handleRemoveProduct(productId, machineId)}
												showDelete={!!canManageSelectedList}
														showCheckbox={true}
														selectedProductIds={selectedProductIds}
														onCheckedChange={handleToggleProductSelection}
											/>
										) : (
											<Box
												display="flex"
												justifyContent="center"
												alignItems="center"
												height="400px"
												flexDirection="column"
												gap={2}
												width="100%"
											>
												<Alert severity="warning">
													{t('ALERT_MESSAGES.CURRENCY_LOADING')}
												</Alert>
												<Typography variant="body2" color="text.secondary">
													Selected Site: {selectedSite || 'None'} | Currency: {selectedSiteCurrency || 'Undefined'}
												</Typography>
											</Box>
										)}
									</Box>
						</>
					)}
				</Box>
			</Box>
			{orderSchedulesEnabled && (
				<ScheduleOrder
					open={scheduleOpen}
					setOpen={setScheduleOpen}
					selectedOrder={transformRecommendationProducts(products)}
					source='Recommendations'
				/>
			)}
		</Box>
	);
}

export default Recommendations;